import { useAuth } from "@/_core/hooks/useAuth";
import MarketingLayout from "@/components/MarketingLayout";
import { TabbedCodeBlock } from "@/components/TabbedCodeBlock";
import { CodeBlock } from "@/components/CodeBlock";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import { getLoginUrl } from "@/const";
import { buildSnippets, secretSnippets } from "@/snippets";
import ReviewBar from "@/components/ReviewBar";
import {
  Shield,
  Lock,
  Clock,
  Bot,
  ArrowRightLeft,
  FolderKey,
} from "lucide-react";
import { useLocation } from "wouter";

const mcpConfigSnippet = `{
  "mcpServers": {
    "double-o": {
      "type": "http",
      "url": "https://oovault.nl/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_AUTH0_TOKEN"
      }
    }
  }
}`;

const features = [
  {
    icon: Bot,
    title: "Native MCP",
    description:
      "Agents get tokens, not secrets. A built-in MCP server lets your AI list and mint credentials — raw values never leave the vault.",
  },
  {
    icon: ArrowRightLeft,
    title: "Request Proxying",
    description:
      "Route API calls through Double O so your agent never touches the secret key directly.",
  },
  {
    icon: FolderKey,
    title: "Environment Bundles",
    description:
      "Group secrets into environments and load them all with a single short-lived token — your whole .env, one call.",
  },
  {
    icon: Lock,
    title: "AES-256-GCM Encryption",
    description:
      "All secrets are encrypted at rest with unique salts per secret. Secure by default, always.",
  },
  {
    icon: Clock,
    title: "Auto-Expiring Tokens",
    description:
      "Credentials expire automatically. No cleanup, no stale keys, no lingering access.",
  },
  {
    icon: Shield,
    title: "IP Allowlists & Audit Trail",
    description:
      "Restrict access by IP and track every secret fetch with a full audit log.",
  },
];

const steps = [
  {
    number: "1",
    title: "Store",
    description: "Drop in your keys. AES-256-GCM encrypted, always.",
  },
  {
    number: "2",
    title: "Scope",
    description:
      "Generate a short-lived token for exactly the secrets you need.",
  },
  {
    number: "3",
    title: "Ship",
    description:
      "Use it in your agent or app. It expires. You don't have to think about it.",
  },
];

export default function Home() {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    setLocation("/secrets");
    return null;
  }

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="bg-gradient-to-b from-background to-muted/20">
        <div className="container py-10 md:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-accent/30 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Bot className="w-4 h-4" />
              Zero Trust for AI Agents
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
              Your AI Agent Can See Your API Keys.
              <br />
              That's a Problem.
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Double O gives your agents short-lived, scoped tokens through
              native MCP — so they get only what they need, and nothing lasts
              forever.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <Button size="lg" className="w-full sm:w-auto" asChild>
                <a href={getLoginUrl()}>Start for Free</a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => {
                  document
                    .getElementById("how-it-works")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                See It in Action
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mt-4">
              Free tier — no card needed
            </p>
          </div>
        </div>
      </section>

      {/* AI-Native / MCP Callout */}
      <section className="bg-muted/30">
        <div className="container py-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
              The Only Secrets Manager Built for AI
            </h2>

            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  Double O speaks{" "}
                  <span className="text-foreground font-medium">
                    MCP natively
                  </span>
                  . Your agents can list secrets and mint short-lived tokens —
                  without ever touching a raw value. Connect once, stay secure
                  forever.
                </p>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Point any MCP-compatible client at{" "}
                  <code className="text-sm bg-muted px-1.5 py-0.5 rounded border border-border">
                    oovault.nl/mcp
                  </code>
                  . Done in 30 seconds.
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { name: "Claude Desktop", hash: "claude-desktop" },
                    { name: "Claude Code", hash: "claude-code" },
                    { name: "Cursor", hash: "cursor" },
                  ].map(({ name, hash }) => (
                    <a
                      key={name}
                      href={`/docs#${hash}`}
                      className="inline-flex items-center gap-1.5 bg-card border border-border rounded-full px-3 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      {name}
                    </a>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  .mcp.json
                </p>
                <CodeBlock code={mcpConfigSnippet} language="json" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="container py-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Secure in Three Steps
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {steps.map((step) => (
              <div key={step.number} className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mx-auto mb-3">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-foreground">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          <div className="max-w-2xl mx-auto">
            <p className="text-sm text-muted-foreground mb-2 text-center">
              One call. That's it.
            </p>
            <TabbedCodeBlock
              snippets={buildSnippets(secretSnippets, "YOUR_TOKEN_HERE")}
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container py-16 border-t border-border">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Built for How AI Teams Actually Work
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <Card key={feature.title}>
                <CardHeader>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-1">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="-mt-2">
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 border-t border-border bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-2 text-foreground">
            Built for Teams That Ship with AI
          </h3>
          <p className="text-center text-muted-foreground mb-4">
            See how teams use Double O to keep their agents and workflows
            secure.
          </p>
          <ReviewBar />
        </div>
      </section>

      {/* Final CTA */}
      <section className="container py-20 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 text-foreground">
            Your API keys shouldn't live in a Slack message.
          </h3>
          <p className="text-lg sm:text-xl text-muted-foreground mb-8">
            Get your vault running in 60 seconds. Free, no credit card.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button size="lg" asChild>
              <a href={getLoginUrl()}>Start for Free</a>
            </Button>
            <a
              href="/docs"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Read the Docs
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
